import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

/**
 * The one Google Maps JS loader: the trip map page, the itinerary's day map and
 * the Place search share a single script load and key. Each loader resolves
 * once its libraries are on `window.google`, and rejects when no key is
 * configured.
 */
const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined

const missingKey = () => Promise.reject(new Error('Missing VITE_GOOGLE_MAPS_API_KEY'))

let optionsSet = false

/** The script takes its options once; a second call is ignored with a warning. */
const setOptionsOnce = (key: string) => {
	if (optionsSet) return
	setOptions({ key, v: 'weekly' })
	optionsSet = true
}

let loaderPromise: Promise<void> | null = null

/** The `maps` and `geometry` libraries, for the trip and day maps. */
export const loadGoogleMaps = (): Promise<void> => {
	if (!apiKey) return missingKey()
	if (!loaderPromise) {
		setOptionsOnce(apiKey)
		loaderPromise = Promise.all([importLibrary('maps'), importLibrary('geometry')]).then(() => undefined)
	}
	return loaderPromise
}

let placesPromise: Promise<void> | null = null

/** The `places` library, for the Place search. A failed load is forgotten, so the next search tries again. */
export const loadGooglePlaces = (): Promise<void> => {
	if (!apiKey) return missingKey()
	if (!placesPromise) {
		setOptionsOnce(apiKey)
		placesPromise = importLibrary('places').then(
			() => undefined,
			error => {
				placesPromise = null
				throw error
			},
		)
	}
	return placesPromise
}

/**
 * What Google draws under the app's own pins, shared by every map. Businesses,
 * civic buildings, bus stops and highway shields are hidden so the trip's pins
 * are the only markers competing for attention; attractions, park names, rail
 * stations and neighbourhood names stay, since they orient someone in a city
 * they don't know. Inline `styles` only apply while the map has no `mapId`.
 */
const HIDDEN_FEATURES = [
	'poi.business',
	'poi.medical',
	'poi.government',
	'poi.school',
	'poi.place_of_worship',
	'transit.station.bus',
] as const

export const baseMapOptions: google.maps.MapOptions = {
	clickableIcons: true,
	styles: [
		...HIDDEN_FEATURES.map(featureType => ({ featureType, stylers: [{ visibility: 'off' }] })),
		{ featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
	],
}

/**
 * The app's map pin: a filled circle with a white ring, drawn as a Symbol so a
 * number can sit on its centre. Call only after {@link loadGoogleMaps} resolves.
 */
export const circleSymbol = (
	radius: number,
	fill: string,
	ring: { color: string; weight: number },
	scale = 1,
): google.maps.Symbol => ({
	path: `M 0,0 m -${radius},0 a ${radius},${radius} 0 1,0 ${radius * 2},0 a ${radius},${radius} 0 1,0 -${radius * 2},0`,
	fillColor: fill,
	fillOpacity: 1,
	strokeColor: ring.color,
	strokeWeight: ring.weight,
	scale,
	anchor: new google.maps.Point(0, 0),
	labelOrigin: new google.maps.Point(0, 0),
})
